import { useEffect } from "react"
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { Columns3, Building2, Users, Radio, CreditCard, Bot, Plus, Plug, Zap, FileSpreadsheet, UserCheck, Receipt } from "lucide-react"
import { cn } from "@/lib/utils"
import { ScopeLabel } from "@/components/ui/ScopeLabel"
import { useAuth } from "@/hooks/useAuth"
import { useTenant } from "@/hooks/useTenant"
import { mensalidadeNoAr, mensalidadeNoArParaPapel } from "@/lib/mensalidade/liberacao"
import { ehOCaminhoDoPagamento, unidadeDoLink } from "@/lib/mensalidade/endereco"
import { AVISO_UNIDADE_SO_DE_LEITURA, unidadeSoDeLeitura } from "@/lib/leituraDaRede"

interface SettingsNavItem {
  label: string
  to: string
  icon: React.ReactNode
  minRole?: "super_admin" | "network_manager" | "tenant_admin" | "backoffice" | "seller" | "ad_agency"
  description: string
  /** O item só existe quando isto responde sim, além da régua de papel (como o
   *  `soQuando` do menu lateral). Hoje, o Pagamento: só no build ligado ao banco
   *  de ensaio, ou para quem opera a cobrança da rede fora dele, até o PM
   *  liberar para todos (`lib/mensalidade/liberacao.ts`). Recebe o papel de
   *  quem vê o menu. */
  soQuando?: (papel: string | undefined) => boolean
}

const settingsNav: SettingsNavItem[] = [
  {
    label: "Funil",
    to: "/configuracoes/funil",
    icon: <Columns3 className="w-4 h-4" />,
    description: "Colunas e estágios",
  },
  {
    label: "ESC",
    to: "/configuracoes/esc",
    icon: <Building2 className="w-4 h-4" />,
    minRole: "tenant_admin",
    description: "Dados da franquia",
  },
  // A mensalidade do CreditOS da unidade, com o PIX (D-B2): quem administra a
  // unidade, e quem opera a cobrança da rede.
  {
    label: "Pagamento",
    to: "/configuracoes/pagamento",
    icon: <Receipt className="w-4 h-4" />,
    minRole: "tenant_admin",
    soQuando: mensalidadeNoArParaPapel,
    description: "Mensalidade do CreditOS",
  },
  {
    label: "Usuários",
    to: "/configuracoes/usuarios",
    icon: <Users className="w-4 h-4" />,
    minRole: "tenant_admin",
    description: "Membros e permissões",
  },
  {
    label: "Exportações",
    to: "/configuracoes/exportacoes",
    icon: <FileSpreadsheet className="w-4 h-4" />,
    minRole: "tenant_admin",
    description: "Quem baixou planilha",
  },
  {
    label: "Captação",
    to: "/configuracoes/captacao",
    icon: <Radio className="w-4 h-4" />,
    minRole: "ad_agency",
    description: "Webhooks e integração",
  },
  {
    label: "Produto",
    to: "/configuracoes/produto",
    icon: <CreditCard className="w-4 h-4" />,
    minRole: "ad_agency",
    description: "Planos e parcelas",
  },
  {
    label: "Agente SDR",
    to: "/configuracoes/sdr",
    icon: <Bot className="w-4 h-4" />,
    minRole: "ad_agency",
    description: "IA conversacional",
  },
  {
    label: "SOMA",
    to: "/configuracoes/soma",
    icon: <Plug className="w-4 h-4" />,
    minRole: "tenant_admin",
    description: "Conexão com ERP",
  },
  // Quem é quem no SOMA (wdwxndy2bm). O endereço não começa com
  // "/configuracoes/soma": o caminho de navegação acha o item pelo começo.
  {
    label: "Vendedores do SOMA",
    to: "/configuracoes/vendedores-do-soma",
    icon: <UserCheck className="w-4 h-4" />,
    minRole: "tenant_admin",
    description: "Quem é quem no SOMA",
  },
  {
    label: "Automações",
    to: "/configuracoes/automacoes",
    icon: <Zap className="w-4 h-4" />,
    minRole: "tenant_admin",
    description: "Regras automáticas do funil",
  },
]

function Breadcrumbs() {
  const location = useLocation()
  const currentItem = settingsNav.find((item) =>
    location.pathname.startsWith(item.to)
  )

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm mb-6">
      <span className="text-[var(--text-secondary)]">Configurações</span>
      {currentItem && (
        <>
          <span className="text-[var(--text-tertiary)]">/</span>
          <span className="font-medium text-[var(--text-primary)]">
            {currentItem.label}
          </span>
        </>
      )}
    </nav>
  )
}

export function SettingsLayout() {
  const { canAccess, can, role } = useAuth()
  const { tenants, viewAll, setSelectedScope, activeTenantId } = useTenant()
  // Leitura da rede (293, wdwxne02aa): as Configurações são telas de mudar a
  // unidade, e numa unidade lida ninguém muda nada. Ela não entra na escolha
  // e, escolhida no seletor do topo, a página diz isso em vez de abrir
  // formulário e botões (regra do PM de 20/10: quem não pode usar não vê).
  const unidadesConfiguraveis = tenants.filter((t) => !t.somente_leitura)
  const soLeitura = !viewAll && unidadeSoDeLeitura(tenants, activeTenantId)
  const location = useLocation()
  const navigate = useNavigate()
  const visibleItems = settingsNav.filter(
    (item) => (!item.minRole || canAccess(item.minRole)) && (!item.soQuando || item.soQuando(role))
  )

  // O `?unidade=<id>` do botão do e-mail da cobrança (D-B3), só na página de
  // Pagamento e só com ela no ar: aplicado ANTES da escolha de unidade daqui,
  // para o link abrir direto na unidade dele. O id de uma unidade da pessoa
  // vira o escopo; qualquer outro valor volta à escolha. Depois, o parâmetro
  // sai do endereço, e o seletor do topo volta a mandar.
  const doLink =
    mensalidadeNoAr() && ehOCaminhoDoPagamento(location.pathname) ? unidadeDoLink(location.search, tenants) : null
  const escopoDoLink = doLink?.escopo ?? null
  const buscaSemAUnidade = doLink?.busca ?? null
  useEffect(() => {
    if (escopoDoLink === null || buscaSemAUnidade === null) return
    setSelectedScope(escopoDoLink)
    navigate({ pathname: location.pathname, search: buscaSemAUnidade, hash: location.hash }, { replace: true, state: location.state })
  }, [escopoDoLink, buscaSemAUnidade, location.pathname, location.hash, location.state, navigate, setSelectedScope])

  // Enquanto o link não foi aplicado, nada da unidade anterior aparece (nem a
  // escolha, nem a página pedindo a lista de outra unidade).
  if (doLink) return null

  if (soLeitura) {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-[var(--text-primary)]">Configurações</h2>
          <ScopeLabel />
        </div>
        <div className="rounded-2xl backdrop-blur-[20px] bg-glass-card border border-glass-border p-8 text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-[rgb(var(--accent-blue-rgb)/0.10)] flex items-center justify-center mx-auto">
            <Building2 className="w-7 h-7 text-[var(--accent-blue,#2563eb)]" />
          </div>
          <p className="text-sm text-[var(--text-secondary)]">{AVISO_UNIDADE_SO_DE_LEITURA}</p>
        </div>
      </div>
    )
  }

  // Com unidade lida na lista, "Todas as unidades" pede a escolha mesmo que
  // sobre uma só: sem ela, a página abriria na unidade corrente, que pode ser
  // uma das lidas.
  if (viewAll && (unidadesConfiguraveis.length > 1 || unidadesConfiguraveis.length < tenants.length)) {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-[var(--text-primary)]">Configurações</h2>
          <ScopeLabel />
        </div>
        <div className="rounded-2xl backdrop-blur-[20px] bg-glass-card border border-glass-border p-8 text-center space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-[rgb(var(--accent-blue-rgb)/0.10)] flex items-center justify-center mx-auto">
            <Building2 className="w-7 h-7 text-[var(--accent-blue,#2563eb)]" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">Selecione uma unidade</h3>
            <p className="text-sm text-[var(--text-secondary)]">Para acessar as configurações, selecione a unidade.</p>
          </div>
          <div className="grid gap-2">
            {unidadesConfiguraveis.map((t) => (
              <button key={t.id} onClick={() => setSelectedScope(t.id)} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-glass-border hover:border-[rgb(var(--accent-blue-rgb)/0.40)] hover:bg-glass-hover transition text-left">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: t.primary_color ?? "#2563eb" }}>{t.name.charAt(0)}</span>
                <span className="text-sm font-medium text-[var(--text-primary)]">{t.name}</span>
              </button>
            ))}
          </div>
          {can("create_esc") && (
            <NavLink
              to="/nova-esc"
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-glass-border hover:border-[rgb(var(--accent-blue-rgb)/0.40)] hover:bg-glass-hover transition text-left"
            >
              <span className="w-8 h-8 rounded-full flex items-center justify-center bg-[rgb(var(--accent-blue-rgb)/0.10)] shrink-0">
                <Plus className="w-4 h-4 text-[var(--accent-blue,#2563eb)]" />
              </span>
              <span className="text-sm font-medium text-[var(--text-secondary)]">Nova ESC</span>
            </NavLink>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row gap-3 md:gap-6 max-w-6xl mx-auto">
      {/* Settings header title (mobile only) */}
      <div className="md:hidden px-1 pb-1">
        <h2 className="text-base font-semibold text-[var(--text-primary)]">
          Configurações
        </h2>
        <ScopeLabel />
      </div>

      {/* Settings sidebar — horizontal scroll on mobile, vertical on desktop */}
      <aside className="w-full md:w-56 shrink-0">
        <div className="rounded-2xl backdrop-blur-[20px] bg-glass-card border border-glass-border overflow-hidden">
          {/* Desktop title */}
          <div className="hidden md:block px-4 pt-4 pb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
              Configurações
            </h2>
            <ScopeLabel />
          </div>
          <nav>
            <ul className="flex md:block overflow-x-auto md:overflow-x-visible scrollbar-none py-2 px-1 md:px-0">
              {visibleItems.map((item) => (
                <li key={item.to} className="shrink-0 md:shrink">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-2.5 text-sm transition-colors whitespace-nowrap md:whitespace-normal rounded-lg md:rounded-none",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)] focus-visible:ring-inset",
                        isActive
                          ? "bg-[rgb(var(--accent-blue-rgb)/0.10)] text-[var(--accent-blue,#2563eb)] font-medium"
                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover"
                      )
                    }
                  >
                    <span className="shrink-0">{item.icon}</span>
                    <div>
                      <p className="leading-tight">{item.label}</p>
                      <p className="hidden md:block text-xs text-[var(--text-tertiary)] leading-tight mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </NavLink>
                </li>
              ))}
            </ul>
            {can("create_esc") && (
              <div className="hidden md:block border-t border-glass-border mx-3 pt-2 pb-2 mt-1">
                <NavLink
                  to="/nova-esc"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)] focus-visible:ring-inset",
                      isActive
                        ? "bg-[rgb(var(--accent-blue-rgb)/0.10)] text-[var(--accent-blue,#2563eb)] font-medium"
                        : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover"
                    )
                  }
                >
                  <Plus className="w-4 h-4 shrink-0" />
                  <div>
                    <p className="leading-tight">Nova ESC</p>
                    <p className="text-xs text-[var(--text-tertiary)] leading-tight mt-0.5">
                      Criar nova franquia
                    </p>
                  </div>
                </NavLink>
              </div>
            )}
          </nav>
        </div>
      </aside>

      {/* Settings content */}
      <div className="flex-1 min-w-0">
        <Breadcrumbs />
        <Outlet />
      </div>
    </div>
  )
}
